import { useEffect, useState } from 'react'

// Kitchen-themed loader for AI generation states.
//
// Replaces the generic blue spinner + "Usually 10-20 seconds" copy
// with a steaming-pot SVG and a cycling list of kitchen-flavored
// status messages that fit the brand. Used wherever an AI call is
// in flight (briefs, recipes, schedules, anomaly scans, etc.).
//
// Props:
//   messages   — array of short status strings to cycle through
//                (defaults to a generic 5-message kitchen rotation)
//   intervalMs — how long each message stays on screen (default 1800)
//   size       — 'sm' | 'md' | 'lg' (controls the pot icon scale)
//   align      — 'col' (centered, vertical) | 'row' (inline, horizontal)
//   tone       — 'light' (default, on white card) | 'dark' (on charcoal
//                hero brief). Controls text color so the cycling status
//                line stays legible against the charcoal #2C2C2A hero.
//
// CSS animations live below in a <style> tag scoped via uniquely-
// named keyframes so they don't collide with anything in Tailwind.
const DEFAULT_MESSAGES = [
  'Prepping the ingredients\u2026',
  'Warming up the burners\u2026',
  'Letting it simmer\u2026',
  'Plating it up\u2026',
  'Almost ready\u2026',
]

export default function KitchenLoader({
  messages = DEFAULT_MESSAGES,
  intervalMs = 1800,
  size = 'md',
  align = 'col',
  tone = 'light',
}) {
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    if (!messages || messages.length <= 1) return
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % messages.length)
    }, intervalMs)
    return () => clearInterval(t)
  }, [messages, intervalMs])

  const dim = size === 'sm' ? 28 : size === 'lg' ? 56 : 40

  // Tone palette. 'dark' is for the charcoal hero brief card so the
  // cycling messages stay legible against #2C2C2A. 'light' (default)
  // is unchanged from the existing call sites on white surfaces.
  const rowTextClass = tone === 'dark' ? 'text-charcoal-text-tertiary' : 'text-black/70'
  const colTextClass = tone === 'dark' ? 'text-charcoal-text-tertiary' : 'text-black/75'

  const wrapperClass =
    align === 'row'
      ? `flex items-center gap-3 text-sm ${rowTextClass}`
      : `flex flex-col items-center gap-3 py-8 text-sm font-semibold ${colTextClass}`

  return (
    <div className={wrapperClass}>
      <KitchenPotIcon dim={dim} />
      <div aria-live="polite" className="text-center">
        {messages[idx]}
      </div>
      <style>{`
        @keyframes rmai-steam {
          0%   { transform: translateY(2px) scale(0.85); opacity: 0; }
          25%  { opacity: 0.85; }
          100% { transform: translateY(-10px) scale(1.15); opacity: 0; }
        }
        @keyframes rmai-pot-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-1px); }
        }
        .rmai-steam-1 { animation: rmai-steam 1.6s ease-out infinite; }
        .rmai-steam-2 { animation: rmai-steam 1.6s ease-out infinite; animation-delay: 0.45s; }
        .rmai-steam-3 { animation: rmai-steam 1.6s ease-out infinite; animation-delay: 0.9s; }
        .rmai-pot     { animation: rmai-pot-bob 2.2s ease-in-out infinite; transform-origin: center; }
      `}</style>
    </div>
  )
}

function KitchenPotIcon({ dim }) {
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Steam puffs */}
      <g>
        <circle cx="18" cy="14" r="2" fill="#fb923c" className="rmai-steam-1" />
        <circle cx="24" cy="12" r="2.5" fill="#f97316" className="rmai-steam-2" />
        <circle cx="30" cy="14" r="2" fill="#fdba74" className="rmai-steam-3" />
      </g>

      {/* Pot */}
      <g className="rmai-pot">
        {/* Lid */}
        <rect x="8"  y="22" width="32" height="3" rx="1.5" fill="#1f2937" />
        {/* Lid knob */}
        <rect x="22" y="19" width="4"  height="3" rx="1"   fill="#1f2937" />
        {/* Body */}
        <path
          d="M10 26 H38 L36 38 Q36 41 33 41 H15 Q12 41 12 38 Z"
          fill="#374151"
        />
        {/* Handles */}
        <rect x="4"  y="28" width="6" height="3" rx="1.5" fill="#1f2937" />
        <rect x="38" y="28" width="6" height="3" rx="1.5" fill="#1f2937" />
        {/* Highlight */}
        <path
          d="M14 28 H34"
          stroke="#fb923c"
          strokeOpacity="0.35"
          strokeWidth="1"
          strokeLinecap="round"
        />
      </g>
    </svg>
  )
}
